// src/GoalsPage.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { getDoc } from "firebase/firestore";
import AddGoalModal from "./AddGoalModal";         // using your existing modal
// import SavingsGoalModal from "./SavingsGoalModal"; // optional alternate

import { auth, db } from "./firebase";
import {
  collection,
  query as fsQuery,
  where,
  getDocs,
  updateDoc,
  doc,
  setDoc,
} from "firebase/firestore";

/* ================= helpers ================= */

// === Goals placement + math ===
const GOALS_CATEGORY_NAME = "Goals";

// Convert YYYY-MM to a date at month end
function lastDayOfMonth(ym) {
  if (!ym) return null;
  const [y, m] = ym.split("-").map(Number);
  if (!y || !m) return null;
  return new Date(y, m, 0);
}

// Compute the monthly contribution needed
function computeMonthlyFromGoal({ targetAmount, currentAmount = 0, targetDate }) {
  const target = Number(targetAmount || 0);
  const current = Number(currentAmount || 0);
  const remain = Math.max(0, target - current);
  const end = lastDayOfMonth(targetDate);
  if (!end || remain === 0) return 0;

  const now = new Date();
  // at least 1 month
  let months =
    (end.getFullYear() - now.getFullYear()) * 12 + (end.getMonth() - now.getMonth());
  if (months < 1) months = 1;
  return Math.ceil((remain / months) * 100) / 100;
}

// Ensure a single “Goals” budget doc exists for this user (type: savings)
async function ensureGoalsDoc(uid) {
  const q = fsQuery(collection(db, "budgets"), where("userId", "==", uid));
  const snap = await getDocs(q);
  const goalsDoc = snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .find(
      (d) =>
        String(d.category || "").toLowerCase() ===
        GOALS_CATEGORY_NAME.toLowerCase()
    );

  if (goalsDoc) return goalsDoc;

  // Create it if missing
  const ref = doc(collection(db, "budgets"));
  await setDoc(ref, {
    userId: uid,
    type: "savings",
    category: GOALS_CATEGORY_NAME,
    subcategories: [],
  });
  return {
    id: ref.id,
    userId: uid,
    type: "savings",
    category: GOALS_CATEGORY_NAME,
    subcategories: [],
  };
}

// Sum monthly totals across all budgets (income vs everything else)
function rollupTotals(budgets = []) {
  let income = 0;
  let outflow = 0;
  budgets.forEach(b => {
    const isIncome = String(b.category || "").toLowerCase() === "income";
    const subs = Array.isArray(b.subcategories) ? b.subcategories : [];
    const sum = subs.reduce((s, sub) => s + Number(sub.amount || 0), 0);
    if (isIncome) income += sum; else outflow += sum;
  });
  return { income, outflow };
}

// Sum contributions into Savings by subcategory name (case-insensitive).
// Assumes contributions are recorded as expenses (negative amounts) in category "Savings".
// We count only negative amounts as "money into goal" -> sum += -amount; others ignored.
async function sumSavingsBySubcategory(uid) {
  const out = new Map(); // key = subcategory name (lowercased), value = total funded (number)
  const snap = await getDocs(fsQuery(collection(db, "transactions"), where("userId", "==", uid)));
  snap.forEach((d) => {
    const t = d.data() || {};
    if (String(t.category || "").toLowerCase() !== "savings") return;
    const sub = (t.subcategory || "").toString().trim().toLowerCase();
    if (!sub) return;
    const amt = Number(t.amount || 0);
    const contrib = amt < 0 ? -amt : 0; // negative expense = contribution out of checking into savings
    if (!contrib) return;
    out.set(sub, (out.get(sub) || 0) + contrib);
  });
  return out;
}

const formatYYYYMM = (yyyyMM) => {
  if (!yyyyMM) return "—";
  const [y, m] = String(yyyyMM).split("-");
  const year = Number(y);
  const monthIdx = Number(m) - 1;
  if (!Number.isFinite(year) || monthIdx < 0 || monthIdx > 11) return String(yyyyMM);
  const monthName = new Date(year, monthIdx, 1).toLocaleString(undefined, { month: "long" });
  return `${monthName}, ${year}`;
};

// Treat a subcategory as a goal if there's a goal map with signal fields
const isGoalSub = (sub) => {
  const g = sub?.goal;
  if (!g) return false;
  if (g.isGoal === true) return true;
  // Backward/alternate schema tolerance
  return ["targetAmount", "targetDate", "name", "currentAmount"].some(
    (k) => g[k] != null && g[k] !== ""
  );
};

const money = (n) =>
  `$${(Number(n || 0)).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function monthsUntilYYYYMM(yyyyMM) {
  if (!yyyyMM) return 1;
  const [y, m] = String(yyyyMM).split("-").map(Number);
  const now = new Date();
  const tgt = new Date(isNaN(y) ? now.getFullYear() : y, isNaN(m) ? now.getMonth() : (m - 1), 1);
  const diff =
    (tgt.getFullYear() - now.getFullYear()) * 12 + (tgt.getMonth() - now.getMonth());
  return Math.max(1, diff);
}

function computeOnTrack(goal) {
  const target = Number(goal.targetAmount || 0);
  const current = Number(goal.currentAmount || 0);
  if (target <= 0) return { onTrack: false, neededMonthly: 0, monthsLeft: 1 };

  const monthsLeft = monthsUntilYYYYMM(goal.targetDate);
  const remaining = Math.max(0, target - current);
  const neededMonthly = Math.ceil(remaining / monthsLeft);

  // Use the subcategory's monthly budget "amount" (string in your data) as the planned contribution
  const plannedMonthly = Number(goal.monthlyBudget || 0);

  // "On track" if the planned monthly >= needed monthly OR already funded
  const onTrack = current >= target || plannedMonthly >= neededMonthly;

  return { onTrack, neededMonthly, monthsLeft, plannedMonthly };
}

const dateKey = (yyyyMM) => {
  if (!yyyyMM) return Number.POSITIVE_INFINITY; // missing dates go last
  const [y, m] = String(yyyyMM).split("-").map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m)) return Number.POSITIVE_INFINITY;
  return y * 100 + m; // e.g., 2025-12 -> 202512
};

const priorityKey = (p) => (p == null ? 99 : Number(p)); // smaller number = higher priority

// --- Responsive helper ---
function useIsMobile(breakpoint = 768) {
  const getMatch = () =>
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia(`(max-width: ${breakpoint}px)`).matches;

  const [isMobile, setIsMobile] = React.useState(getMatch());

  React.useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const handler = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler); // Safari fallback
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, [breakpoint]);

  return isMobile;
}

/* Goal rows come from Savings budget doc's subcategories[] where sub.goal.isGoal === true */
export default function GoalsPage() {
  const [goals, setGoals] = useState([]); // [{id, budgetId, subIndex, name, targetAmount, currentAmount, targetDate, notes, priority, monthlyBudget}]
  const [expandedId, setExpandedId] = useState(null);

  const [didApplyFocus, setDidApplyFocus] = useState(false);

  const [allBudgets, setAllBudgets] = useState([]);
  const [overGuard, setOverGuard] = useState({ open: false, overBy: 0, pending: null });

  const [showModal, setShowModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null); // same shape as items in goals[]
  const [savingsBudgetDoc, setSavingsBudgetDoc] = useState(null); // {id, subcategories: [...]}

  const [confirmDelete, setConfirmDelete] = useState({ open: false, goal: null });

  // Sort: name | priority | date
  const [sortBy, setSortBy] = useState("name"); // default A–Z

  const rowRefs = useRef({});

  const isMobile = useIsMobile(768);

  // Reload all goals for the signed-in user
  const reloadGoals = useCallback(async () => {
    const uid = auth.currentUser?.uid;
    if (!uid) {
        setGoals([]);
        setSavingsBudgetDoc(null);
        return;
    }

    // Load all budget docs for this user
    const snap = await getDocs(
        fsQuery(collection(db, "budgets"), where("userId", "==", uid))
    );

    let collected = [];
    let hostSavingsDoc = null;

    snap.forEach((d) => {
        const data = d.data() || {};
        const subs = Array.isArray(data.subcategories) ? data.subcategories : [];
        const isSavingsDoc =
        String(data.type || "").toLowerCase() === "savings" ||
        String(data.category || "").toLowerCase() === "savings";

        if (!hostSavingsDoc && isSavingsDoc) {
        hostSavingsDoc = { id: d.id, ...data };
        }

        subs.forEach((sub, i) => {
        if (!isGoalSub(sub)) return;
        const g = sub.goal || {};
        collected.push({
            id: `${d.id}:${i}`,
            budgetId: d.id,
            subIndex: i,
            name: g.name || sub.name || "(Untitled goal)",
            targetAmount: Number(g.targetAmount || 0),
            currentAmount: Number(g.currentAmount || 0),
            targetDate: g.targetDate || "",
            notes: g.notes || "",
            priority: g.priority ?? 2,
            monthlyBudget: Number(sub.amount || 0),
            _rawSub: sub,
        });
        });
    });

    // NEW: keep the raw budgets for guardrail math
    setAllBudgets(snap.docs.map((d) => ({ id: d.id, ...d.data() })));

    // 1) Pull Savings contributions by subcategory (lowercased)
    const fundedBySubLower = await sumSavingsBySubcategory(uid);

    // 2) Overlay the computed totals onto UI (fallback to stored currentAmount if no txs yet)
    const merged = collected.map((g) => {
    const key = (g.name || "").toLowerCase(); // sub.name mirrors goal name on save
    const txSum = fundedBySubLower.get(key);
    return { ...g, currentAmount: txSum != null ? txSum : g.currentAmount };
    });

    // 3) Sort after merge (your render also resorts based on the header dropdown)
    merged.sort((a, b) =>
    (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
    );

    // 4) Commit to state
    setGoals(merged);
    setSavingsBudgetDoc(hostSavingsDoc || null);
  }, []);

  useEffect(() => {
    reloadGoals();
  }, [reloadGoals]);

  const openCreate = () => {
    setEditingGoal(null);
    setShowModal(true);
  };

  const openEdit = (goal) => {
    setEditingGoal(goal);
    setShowModal(true);
  };

  const closeModal = () => {
    setEditingGoal(null);
    setShowModal(false);
  };

  // Save handler for modal (both create & edit)
  // Save handler for modal (both create & edit)
  const saveGoal = async (payload) => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;

    // 1) compute the new monthly needed
    const newMonthly = computeMonthlyFromGoal(payload);

    // 2) if editing, compute delta vs previous; else full amount is delta
    const oldMonthly = editingGoal ? Number(editingGoal.monthlyBudget || 0) : 0;
    const delta = newMonthly - oldMonthly;

    // 3) over-budget guardrail (based on allBudgets currently in state)
    const { income, outflow } = rollupTotals(allBudgets);
    const overBy = Math.max(0, outflow + delta - income);
    if (overBy > 0) {
        setOverGuard({
            open: true,
            overBy,
            pending: { payload, newMonthly },
        });
        return;
    }

    // 4) persist immediately if not over
    await persistGoal(uid, payload, newMonthly);
    await reloadGoals();
    setExpandedId(null);
    closeModal();
    };

    // single writer for both create & edit
    async function persistGoal(uid, payload, monthly) {
    if (editingGoal) {
        // UPDATE in-place where it currently lives (don’t move categories during edit)
        const budgetRef = doc(db, "budgets", editingGoal.budgetId);
        const snap = await getDoc(budgetRef);
        const data = snap.data() || {};
        const subs = Array.isArray(data.subcategories) ? [...data.subcategories] : [];
        const idx = editingGoal.subIndex;
        const prev = { ...(subs[idx] || {}) };

        subs[idx] = {
        ...prev,
        name: payload.name,
        amount: String(monthly), // write the monthly into the budget
        type: "savings",
        goal: {
            ...(prev.goal || {}),
            ...payload,
            isGoal: true,
            userId: (prev.goal && prev.goal.userId) || uid,
            type: (prev.goal && prev.goal.type) || "savings",
        },
        };

        await updateDoc(budgetRef, { subcategories: subs });
    } else {
        // CREATE under the unified “Goals” doc
        const goalsDoc = await ensureGoalsDoc(uid);
        const ref = doc(db, "budgets", goalsDoc.id);

        // refresh latest subcategories before appending
        const latest = await getDoc(ref);
        const cur = latest.data() || {};
        const subs = Array.isArray(cur.subcategories) ? [...cur.subcategories] : [];

        subs.push({
        name: payload.name,
        amount: String(monthly), // write the monthly into the budget
        type: "savings",
        goal: {
            ...payload,
            isGoal: true,
            userId: uid,
            type: "savings",
        },
        });

        await updateDoc(ref, { subcategories: subs });
    }
  }

  function OverBudgetModal({ overBy, onReview, onProceed, onCancel }) {
    return (
        <div style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        backdropFilter: "blur(3px)",      // ← blur whatever's underneath (including AddGoalModal)
        WebkitBackdropFilter: "blur(3px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2147483000                // ← above any portal/modal stacks
        }}>
        <div
          style={{
            background: "#fff",
            borderRadius: 10,
            padding: 20,
            width: "min(420px, 92vw)",
            boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
          }}
        >
            <h3 style={{ marginTop: 0 }}>Over your overall budget</h3>
            <p>This change puts you over your overall monthly budget by <b>${overBy.toFixed(2)}</b>.</p>
            <p>Please revise your budget to get back in line.</p>
            <div 
              style={{
                display: "flex",
                gap: 8,
                justifyContent: "flex-end",
                marginTop: 12,
                flexWrap: "wrap",
              }}
            >
                {/* Edit goal: just close the guard and leave the Add Goal modal open */}
                <button onClick={onCancel}>Edit Goal</button>

                {/* Set Goal + Review Budget Page: save the goal, close both, go to Budget */}
                <button
                    onClick={onReview}
                    style={{ background: "#F4C542", border: "1px solid #F4C542", color: "#222", padding: "6px 10px", borderRadius: 6 }}
                >
                    Save and Review Budget Page
                </button>

                {/* Save and return to Goals Page: save the goal and close both modals */}
                <button
                    onClick={onProceed}
                    style={{ background: "#00B074", border: "1px solid #00B074", color: "#fff", padding: "6px 10px", borderRadius: 6 }}
                >
                    Save and return to Goals Page
                </button>
            </div>
        </div>
        </div>
    );
    }

  const askDelete = (goal) => setConfirmDelete({ open: true, goal });
  const cancelDelete = () => setConfirmDelete({ open: false, goal: null });

  const confirmDeleteGoal = async () => {
    const g = confirmDelete.goal;
    if (!g) return cancelDelete();

    const budgetRef = doc(db, "budgets", g.budgetId);
    const snap = await getDoc(budgetRef);
    const data = snap.data();
    if (!data) return cancelDelete();

    const subs = Array.isArray(data.subcategories) ? [...data.subcategories] : [];
    const sub = { ...(subs[g.subIndex] || {}) };
    const goal = { ...(sub.goal || {}) };

    // Remove as goal but keep the savings subcategory alive
    // (This mirrors your storage model where goals live inside savings subcategories.)
    sub.goal = { ...goal, isGoal: false };
    subs[g.subIndex] = sub;

    await updateDoc(budgetRef, { subcategories: subs });

    await reloadGoals();
    setExpandedId(null);
    cancelDelete();
  };

  const rows = useMemo(() => {
    return goals.map((it) => {
      const { onTrack, neededMonthly, monthsLeft, plannedMonthly } = computeOnTrack(it);
      return { ...it, onTrack, neededMonthly, monthsLeft, plannedMonthly };
    });
  }, [goals]);

  const sortedRows = useMemo(() => {
    const arr = [...rows];
    if (sortBy === "priority") {
        arr.sort(
        (a, b) =>
            (a.priority ?? 99) - (b.priority ?? 99) ||
            (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
        );
    } else if (sortBy === "date") {
        const dateKey = (yyyyMM) => {
        if (!yyyyMM) return Number.POSITIVE_INFINITY;
        const [y, m] = String(yyyyMM).split("-").map(Number);
        if (!Number.isFinite(y) || !Number.isFinite(m)) return Number.POSITIVE_INFINITY;
        return y * 100 + m;
        };
        arr.sort(
        (a, b) =>
            dateKey(a.targetDate) - dateKey(b.targetDate) ||
            (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
        );
    } else {
        // name (A–Z)
        arr.sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
        );
    }
    return arr;
  }, [rows, sortBy]);

  useEffect(() => {
    // Only run this once per mount
    if (didApplyFocus) return;

    const params = new URLSearchParams(window.location.search);
    const focusId = params.get("focusId");
    const focusName = params.get("focus");

    if (!sortedRows || sortedRows.length === 0) return;

    let target = null;
    if (focusId) {
        target = sortedRows.find((g) => g.id === focusId);
    } else if (focusName) {
        const needle = String(focusName).toLowerCase();
        target = sortedRows.find((g) => String(g.name || "").toLowerCase() === needle);
    }

    if (target) {
        // Expand the target and then scrub the URL so it won't re-expand next time
        setExpandedId(target.id);

        // Remove just our focus params, keep any others
        const next = new URLSearchParams(window.location.search);
        next.delete("focusId");
        next.delete("focus");
        const newUrl =
        window.location.pathname +
        (next.toString() ? `?${next.toString()}` : "") +
        (window.location.hash || "");
        window.history.replaceState({}, "", newUrl);
    } else {
        // No focus params => ensure collapsed on first visit
        setExpandedId(null);
    }

    setDidApplyFocus(true);
  }, [sortedRows, didApplyFocus]);

  return (
    <div
      style={{
        marginLeft: isMobile ? 0 : "-5px",
        padding: isMobile ? "0.75rem 0.75rem 4.5rem" : "1.5rem 1.5rem 4.5rem",
      }}
    >
      {/* Sticky header */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          background: "#fdfdfd",
          paddingTop: isMobile ? "calc(env(safe-area-inset-top, 0px) + 8px)" : undefined,
          paddingBottom: isMobile ? "0.5rem" : "0.75rem",
          marginBottom: "0.75rem",
          borderBottom: "1px solid #eee",
        }}
      >
        <h2 style={{ marginBottom: 4 }}>Goals</h2>
        <p style={{ color: "#555", margin: 0 }}>
          Track your savings goals. Click a goal to see details or edit it.
        </p>

        <div 
          style={{
            marginTop: "0.75rem",
            display: "flex",
            alignItems: "stretch",
            gap: "0.75rem",
            flexWrap: isMobile ? "wrap" : "nowrap",
          }}
        > 
            <button
                onClick={openCreate}
                style={{
                  background: "#00B074",
                  color: "#fff",
                  border: "none",
                  padding: "0.55rem 0.9rem",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontWeight: 600,
                  width: isMobile ? "100%" : "auto",
                  touchAction: "manipulation",
                }}
            >
                + Create Goal
            </button>

            {/* Sort control */}
            <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <span style={{ color: "#555" }}>Sort by:</span>
                <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{
                  border: "1px solid #ddd",
                  borderRadius: 8,
                  padding: "0.4rem 0.6rem",
                  background: "#fff",
                  cursor: "pointer",
                  width: isMobile ? "100%" : "auto",
                }}
                >
                <option value="name">Name (A–Z)</option>
                <option value="priority">Priority (high → low)</option>
                <option value="date">Goal date (soonest first)</option>
                </select>
            </label>
        </div>
      </div>

      {/* Striped list */}
      {sortedRows.length === 0 ? (
        <div style={{ color: "#666" }}>No goals yet.</div>
      ) : (
        <div
          style={{
            border: "1px solid #eee",
            borderRadius: isMobile ? 6 : 8,
            overflow: "hidden",
          }}
        >
          {sortedRows.map((g, idx) => {
            const rowBg = idx % 2 === 0 ? "#fff" : "#f7f7f7";
            const headline = `${g.name} — ${money(g.currentAmount)} funded of ${money(g.targetAmount)} goal`;
            const status = g.currentAmount >= g.targetAmount
              ? "Achieved"
              : g.onTrack
              ? "On track"
              : "Off track";

            return (
              <div
                key={g.id}
                ref={(el) => (rowRefs.current[g.id] = el)}
                style={{ background: rowBg }}
              >
                {/* Row header */}
                <div
                 style={{
                    padding: isMobile ? "0.6rem 0.75rem" : "0.5rem 0.75rem",
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    alignItems: isMobile ? "stretch" : "center",
                    justifyContent: isMobile ? "flex-start" : "space-between",
                    gap: isMobile ? "0.5rem" : 0,
                    cursor: "pointer",
                    borderTop: idx === 0 ? "none" : "1px solid #eee",
                  }}
                >
                  <button
                    onClick={() => setExpandedId(expandedId === g.id ? null : g.id)}
                    style={{
                      background: "none",
                      border: "none",
                      textAlign: "left",
                      fontSize: isMobile ? "0.98rem" : "1rem",
                      cursor: "pointer",
                      padding: 0,
                      lineHeight: 1.3,
                      touchAction: "manipulation",
                    }}
                  >
                    <strong>{headline}</strong>
                    <div style={{ fontSize: "0.85rem", color: "#666", marginTop: 2, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        <span>{status}</span>
                        <span
                            style={{
                            display: "inline-block",
                            padding: "0.1rem 0.4rem",
                            borderRadius: 999,
                            border: "1px solid #e5e5e5",
                            background: "#f7f7f7",
                            fontSize: "0.8rem",
                            lineHeight: 1.2
                            }}
                            title="Priority (smaller number = higher priority)"
                        >
                            Priority {g.priority ?? 2}
                        </span>
                        <span>• needs ~{money(g.neededMonthly)} /mo to hit by {formatYYYYMM(g.targetDate)}</span>
                    </div>
                  </button>

                  <div 
                    style={{
                      display: "flex",
                      gap: "0.5rem",
                      width: isMobile ? "100%" : "auto",
                    }}
                  >
                    <button
                      onClick={() => openEdit(g)}
                      style={{
                        background: "#00B074",
                        color: "white",
                        border: "none",
                        padding: isMobile ? "0.55rem 0.75rem" : "0.35rem 0.75rem",
                        borderRadius: 6,
                        cursor: "pointer",
                        flex: isMobile ? 1 : undefined,
                        minHeight: isMobile ? 40 : undefined,
                        touchAction: "manipulation",
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => askDelete(g)}
                      style={{
                        background: "#d33",
                        color: "white",
                        border: "none",
                        padding: isMobile ? "0.55rem 0.75rem" : "0.35rem 0.75rem",
                        borderRadius: 6,
                        cursor: "pointer",
                        flex: isMobile ? 1 : undefined,
                        minHeight: isMobile ? 40 : undefined,
                        touchAction: "manipulation",
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {/* Expanded details */}
                {expandedId === g.id && (
                  <div style={{ background: rowBg, padding: "0.6rem 0.75rem 0.8rem", borderTop: "1px solid #eee" }}>
                    <div 
                      style={{
                        display: "grid",
                        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                        gap: "1rem",
                        rowGap: isMobile ? "0.75rem" : "1rem",
                      }}
                    >
                      <div>
                        <h4 style={{ margin: 0 }}>Details</h4>
                        <ul style={{ margin: "0.5rem 0 0 1rem" }}>
                          <li>
                            <strong>Target:</strong> {money(g.targetAmount)} by {formatYYYYMM(g.targetDate)}
                          </li>
                          <li>
                            <strong>Current:</strong> {money(g.currentAmount)}
                          </li>
                          <li>
                            <strong>Planned monthly (budget):</strong> {money(g.monthlyBudget)}
                          </li>
                          <li>
                            <strong>Needed monthly:</strong> {money(g.neededMonthly)} for next {g.monthsLeft} month
                            {g.monthsLeft === 1 ? "" : "s"}
                          </li>
                          <li>
                            <strong>Priority:</strong> {g.priority ?? 2}
                          </li>
                          {g.notes && (
                            <li>
                              <strong>Notes:</strong> {g.notes}
                            </li>
                          )}
                        </ul>
                      </div>

                      <div>
                        <h4 style={{ margin: 0 }}>Tracking</h4>
                        <div style={{ color: "#555", marginTop: 8, fontSize: isMobile ? "0.95rem" : undefined }}>
                            This page estimates progress using the goal’s <em>current amount</em> and the monthly amount you’ve budgeted
                            for this savings subcategory. As you move money into this goal and update the current amount, the on-track
                            status will update automatically.
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {overGuard.open && (
        <OverBudgetModal
            overBy={overGuard.overBy}

            // Edit Goal → close guard, keep Add Goal modal open for changes
            onCancel={() => setOverGuard({ open: false, overBy: 0, pending: null })}

            // Set Goal + Review Budget Page → save, close both, navigate to Budget
            onReview={async () => {
            const { payload, newMonthly } = overGuard.pending || {};
            if (payload) {
                await persistGoal(auth.currentUser?.uid, payload, newMonthly);
                // optional: await reloadGoals();
            }
            setOverGuard({ open: false, overBy: 0, pending: null });
            closeModal();                 // close the Add Goal modal
            window.location.href = "/budget";
            }}

            // Save and return to Goals Page → save, close both, stay on Goals
            onProceed={async () => {
            const { payload, newMonthly } = overGuard.pending || {};
            if (payload) {
                await persistGoal(auth.currentUser?.uid, payload, newMonthly);
                await reloadGoals();
            }
            setOverGuard({ open: false, overBy: 0, pending: null });
            setExpandedId(null);
            closeModal();                 // close the Add Goal modal
            }}
        />
      )}

      {/* Modal (create/edit) */}
      {showModal && (
        <AddGoalModal
          onClose={closeModal}
          onSave={(payload) => {
            // Normalize numbers to strings for Firestore (to match your existing schema)
            const normalized = {
              name: payload.name,
              targetAmount: String(payload.targetAmount),
              targetDate: payload.targetDate,
              priority: Number(payload.priority || 2),
              notes: payload.notes || "",
              currentAmount: String(payload.currentAmount || 0),
            };
            saveGoal(normalized);
          }}
          initialData={
            editingGoal
              ? {
                  name: editingGoal.name,
                  targetAmount: editingGoal.targetAmount,
                  targetDate: editingGoal.targetDate,
                  priority: editingGoal.priority ?? 2,
                  notes: editingGoal.notes || "",
                  currentAmount: editingGoal.currentAmount || 0,
                }
              : undefined
          }
        />
      )}

      {/* Delete confirm */}
      {confirmDelete.open && (
        <div
          onClick={cancelDelete}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1200,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              padding: "1rem 1.25rem",
              borderRadius: 12,
              width: 520,
              maxWidth: "90%",
            }}
          >
            <h3 style={{ marginTop: 0 }}>Delete this goal?</h3>
            <p style={{ color: "#555" }}>
              <strong>{confirmDelete.goal?.name}</strong>
            </p>
            <p style={{ color: "#666" }}>
              This will remove it from the Goals list. The Savings subcategory will remain in your budget.
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
              <button
                onClick={cancelDelete}
                style={{ background: "#eee", border: "none", padding: "0.45rem 0.9rem", borderRadius: 6, cursor: "pointer" }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteGoal}
                style={{ background: "#d33", color: "#fff", border: "none", padding: "0.45rem 0.9rem", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}
                >
                Delete goal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
